import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { calcRangeExtent } from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import ActionsContext from "common/ActionsContext";
import { hoursScopeChoices } from "common/constants";
import { memoryUsageSystemDateRetrieval } from "./api";
import BqnContext from "common/BqnContext";
import { MemoryChart } from "./MemoryChart";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import { MemorySummary } from "./MemorySummary";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const RangeContainer = styled.div`
  margin: 20px 0;
`;

const SysStatsMemReport = ({ returnView = null }) => {
  // Opened from a view that keeps its hours, such as the dashboard, the
  // report covers them. Otherwise, one day.
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [hours, setHours] = useState(hoursOfReturnView ?? 24);
  const [totals, setTotals] = useState({ System: null, Mpool: null, Dpdk: [] });
  const [initialRange, setInitialRange] = useState();
  const [range, setRange] = useState();
  const actions = useContext(ActionsContext);
  const { System, Mpool, Dpdk } = totals;
  const { loginBqnId, bqnId, onlineBqns, clusterStatus } =
    useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const bqnToUse = bqnId === "all" ? loginBqnId : bqnId;
  const shouldShowGrid =
    mode === "cluster" && bqnId === "all" && onlineBqns.length > 1;

  const doLoad = () => {
    setRange(undefined);
    setTotals({ System: null, Mpool: null, Dpdk: [] });
    memoryUsageSystemDateRetrieval(bqnToUse, mode, bqnId)
      .then(([System, Mpool, Dpdk, SysDate]) => {
        setTotals({ System, Mpool, Dpdk });
        setInitialRange(calcRangeExtent(SysDate, hours));
      })
      .catch((error) => {
        console.warn(
          `Memory usage retrieval failed: ${
            !error.message ? "unknown reason" : error.message
          }`,
        );
      });
  };

  useEffect(() => actions.recv("do-load", doLoad), [doLoad]);

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doLoad();
  }, [hours, mode, bqnId]);

  return (
    <Wrapper>
      <ArrangeInColumns rowGap="0">
        <SelectWrapper>
          <SelectInputWithIcon
            title="Date Range"
            name="Date Range"
            icon="date_range"
            selected={hours}
            onChange={({ target }) => setHours(parseInt(target.value))}
            options={hoursScopeChoices}
          />
        </SelectWrapper>
      </ArrangeInColumns>

      {totals.System !== null && initialRange && !shouldShowGrid && (
        <ColumnsGrid columns={1} minWidth="7cm" gap="0px">
          {mode === "cluster" && bqnId !== "all" && (
            <h4 className="margin-b-0"> BQN {bqnId} </h4>
          )}
          <MemoryChart
            totals={totals}
            bqn={bqnToUse}
            bqnIdAsked={bqnId}
            range={range}
            initialRange={initialRange}
            hours={hours}
            big={true}
          />
          <MemorySummary
            bqnId={bqnToUse}
            System={System}
            Mpool={Mpool}
            Dpdk={Dpdk}
          />
        </ColumnsGrid>
      )}
      {totals.System !== null && initialRange && shouldShowGrid && (
        <ColumnsGrid columns={2} minWidth="7cm" gap="0px">
          {onlineBqns.map((bqn) => (
            <div key={bqn.bqnId}>
              <h4 className="margin-b-0"> BQN {bqn.bqnId} </h4>
              <MemoryChart
                totals={totals}
                bqn={bqn.bqnId}
                bqnIdAsked={bqn.bqnId}
                range={range}
                initialRange={initialRange}
                hours={hours}
                big={false}
              />
              <MemorySummary
                bqnId={bqn.bqnId}
                System={System}
                Mpool={Mpool}
                Dpdk={Dpdk}
              />
            </div>
          ))}
        </ColumnsGrid>
      )}
      <RangeContainer>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </RangeContainer>
    </Wrapper>
  );
};

export default SysStatsMemReport;
